import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { WorkflowBuilderApiService } from '../services/workflow-builder-api.service';
import { workflowStepLabel } from '../models/workflow-labels';

/**
 * Nombre legible de un NODO a partir de su id numérico.
 *
 * Las pantallas de ejecución (inscripciones, monitoreo, cola de esperas, timeline) solo reciben
 * `currentNodeId` / `nodeId` / `waitNodeId`: un entero de la tabla `workflow_node`. Se pintaba tal
 * cual («Nodo actual: 481»), que no le dice nada a nadie. El grafo del editor —que estas pantallas
 * ya pueden pedir— sí tiene el título que escribió el usuario y el subtipo del nodo, así que aquí
 * se carga UNA vez por workflow y se cachea para todas las filas de la tabla.
 *
 * Si la carga falla (permisos, workflow borrado) el mapa queda vacío y las plantillas caen al
 * «Nodo #id», que es lo que se mostraba antes: nunca se rompe la pantalla por una etiqueta.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowNodeLabelService {
  private readonly names = new Map<number, Map<number, string>>();
  private readonly inFlight = new Map<number, Observable<Map<number, string>>>();

  constructor(private builderApi: WorkflowBuilderApiService) {}

  /** Carga (y cachea) los nombres de nodo del workflow. Idempotente. */
  load(workflowId: number): Observable<Map<number, string>> {
    const cached = this.names.get(workflowId);
    if (cached) {
      return of(cached);
    }
    const pending = this.inFlight.get(workflowId);
    if (pending) {
      return pending;
    }
    const request = this.builderApi.getGraph(workflowId).pipe(
      map((graph) => {
        const dictionary = new Map<number, string>();
        for (const node of graph.nodes ?? []) {
          const title = node.title?.trim();
          dictionary.set(node.id, title || workflowStepLabel(node.nodeType, node.nodeSubtype, `Nodo #${node.id}`));
        }
        return dictionary;
      }),
      catchError(() => of(new Map<number, string>())),
      tap((dictionary) => {
        this.names.set(workflowId, dictionary);
        this.inFlight.delete(workflowId);
      }),
      shareReplay(1)
    );
    this.inFlight.set(workflowId, request);
    return request;
  }

  /**
   * Etiqueta del nodo ya cacheada. Devuelve «Nodo #id» mientras el grafo no haya llegado o si el
   * nodo ya no existe (versión antigua del workflow), y `fallback` cuando no hay id.
   */
  labelFor(workflowId: number, nodeId: number | null | undefined, fallback = '—'): string {
    if (nodeId === null || nodeId === undefined) {
      return fallback;
    }
    return this.names.get(workflowId)?.get(nodeId) ?? `Nodo #${nodeId}`;
  }
}
